/**
 * Pixel-art sprite representation and blitting helpers.
 *
 * A sprite is authored by hand as one string per row, one character per pixel,
 * with characters indexing `PALETTE`. `.` is transparent. Rows are top to
 * bottom. This keeps art reviewable in source control as text, which matters
 * far more than build tooling for a game of this size.
 */

import { colorOf } from './palette'

export interface Sprite {
  w: number
  h: number
  /** `h` strings, each `w` characters. */
  data: string[]
}

/** Build a sprite, validating its dimensions. Throws on malformed data. */
export function sprite(w: number, h: number, data: string[]): Sprite {
  if (data.length !== h) {
    throw new Error(`sprite: expected ${h} rows, got ${data.length}`)
  }
  for (let y = 0; y < h; y++) {
    const row = data[y]
    if (row === undefined || row.length !== w) {
      throw new Error(
        `sprite: row ${y} width ${row?.length ?? 0}, expected ${w}`,
      )
    }
  }
  return { w, h, data }
}

/**
 * Parse an indented multi-line string into a Sprite.
 *
 * Art convention: a transparent pixel is always `.`, never a space. That makes
 * leading whitespace unambiguously indentation, so we strip *all* of it from
 * every row rather than guessing at a common indent.
 *
 * This matters more than it looks. A previous version stripped a fixed two
 * spaces, which silently depended on how the surrounding code happened to be
 * indented — so running a formatter over the file shifted every sprite by one
 * column and broke the art. Stripping all leading whitespace is immune to that.
 */
export function parseSprite(width: number, art: string): Sprite {
  const lines = art
    .split('\n')
    // Drop all leading whitespace (indentation) and trailing whitespace.
    .map((l) => l.replace(/^\s+/, '').replace(/\s+$/, ''))
    // Discard blank rows at either end. A template literal written as
    //
    //     parseSprite(16, `
    //       ..KKKK..
    //     `)
    //
    // naturally starts with a newline, and without this that leading blank
    // becomes a zero-width first row and throws. Trimming both ends means the
    // art can be laid out the readable way without tripping the validator.
    .filter((l, i, arr) => l !== '' || (i > 0 && i < arr.length - 1))
  return sprite(width, lines.length, lines)
}

/**
 * Recolour a sprite by remapping palette characters.
 *
 * Used to derive power-up sprites from a base silhouette. Deriving is strictly
 * better than hand-drawing these: a derived sprite can never drift out of sync
 * with its source, and it is impossible to introduce an off-by-one row.
 *
 * Characters absent from `map` pass through unchanged, so outlines and
 * transparent pixels survive.
 */
export function tint(s: Sprite, map: Record<string, string>): Sprite {
  return {
    w: s.w,
    h: s.h,
    data: s.data.map((row) => [...row].map((ch) => map[ch] ?? ch).join('')),
  }
}

/**
 * Draw a sprite into a 2D context at integer pixel coordinates.
 * Nearest-neighbour is enforced by the canvas CSS, not here.
 */
export function drawSprite(
  ctx: CanvasRenderingContext2D,
  s: Sprite,
  dx: number,
  dy: number,
  flip = false,
  alpha = 1,
): void {
  ctx.save()
  ctx.globalAlpha = alpha
  if (flip) {
    ctx.translate(dx + s.w, dy)
    ctx.scale(-1, 1)
    dx = 0
  }
  // Batch by colour to minimise state changes.
  for (let y = 0; y < s.h; y++) {
    const row = s.data[y]
    if (row === undefined) continue
    let runStart = -1
    let runColor: string | null = null
    for (let x = 0; x <= s.w; x++) {
      const ch = x < s.w ? row[x] : undefined
      const col = ch === undefined ? null : colorOf(ch)
      if (col !== runColor) {
        if (runColor !== null && runStart >= 0) {
          ctx.fillStyle = runColor
          ctx.fillRect(dx + runStart, dy + y, x - runStart, 1)
        }
        runStart = x
        runColor = col
      }
    }
  }
  ctx.restore()
}
